import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';

/**
 * Get value of meta tag which has prefix `ajs-` in key.
 * @param key - key of the meta tag. Should not add a prefix `ajs-` in the argument.
 */
export const getMeta = (key: string): string | undefined => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const metaTag = document.querySelector(`meta[name='ajs-${key}']`);
	return metaTag === null ? undefined : metaTag.getAttribute('content') || undefined;
};

export const getJQuery = () => AJS.$;

export const getGlobalAP = (): Window['_AP'] =>
	// In the future, we can add analytics events to know how this can fail to access the global variable.
	// We can improve it to load WRM in async
	// Just to add some context for future reviewers: `window._AP` is the actual Connect module that will no longer be loaded via WRM.
	// Its properties are both appended from modules like the dialog options and the public @atlassian/atlassian-connect-js library.

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	window._AP;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isObject = <T extends Record<PropertyKey, any> = Record<PropertyKey, unknown>>(
	obj?: T | unknown,
): obj is T => typeof obj === 'function' || (typeof obj === 'object' && !!obj);

export const escapeSelector = (s?: string | null): string => {
	if (!s) {
		throw new Error('No selector to escape');
	}

	return s.replace(/[!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~]/g, '\\$&');
};

const FIVE_MINUTES = 60 * 1000 * 5;

/**
 * Wait for jQuery, from Critical Heritage, to be present on the page. We can't
 * use WRM to check for the module being registered because it's a private module and
 * it gets registered asynchronously inside its batch.
 * Connect needs to wait for jQuery as `@atlassian/atlassian-connect-js` uses it to provide the
 * extension elements to the events.
 */
export const waitForJQuery = (): Promise<void> =>
	new Promise((resolve, reject) => {
		const interval = setInterval(() => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			if (window.AJS?.$) {
				clearInterval(interval);
				resolve();
			}
		}, 10);

		setTimeout(() => {
			clearInterval(interval);
			fireErrorAnalytics({
				meta: {
					id: 'connect-jquery-timeout',
					packageName: 'jiraConnect',
				},
				error: new Error('Timeout when waiting for jQuery to be available on the page'),
				// this is an extremely common error, so we don't want to spam Sentry with it
				skipSentry: true,
			});
			reject();
		}, FIVE_MINUTES);
	});

export const WIDGET_STAGING_ADDON_KEY = 'com.atlassian.servicedesk.embedded-dev';
export const WIDGET_PROD_ADDON_KEY = 'com.atlassian.servicedesk.embedded';
export const ErrorDebugAddonKeys = [WIDGET_PROD_ADDON_KEY, WIDGET_STAGING_ADDON_KEY];

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { FetchConnectError, handleConnectError } from './fetch-connect-error';
